(() => {
  'use strict';
  const launch = () => {
  const root = document.querySelector('[data-finiks-consent]');
  if (!root) return;
  const purposes = ['analytics', 'behaviour', 'personalisation', 'measurement', 'attribution', 'camera', 'microphone'];
  const isGranted = value => value === true || value === 'yes';
  const isDecision = value => value === true || value === false || value === 'yes' || value === 'no';
  const banner = root.querySelector('[data-finiks-consent-banner]');
  const preferences = root.querySelector('[data-finiks-consent-preferences]');
  const status = root.querySelector('[data-finiks-consent-status]');
  const controls = root.querySelectorAll('button');
  const customerPrivacy = () => window.Shopify && window.Shopify.customerPrivacy;
  const availableCustomerPrivacy = () => {
    const api = customerPrivacy();
    return api && typeof api.currentVisitorConsent === 'function' && typeof api.setTrackingConsent === 'function' ? api : null;
  };
  let customerPrivacyLoad = null;
  const ensureCustomerPrivacy = () => {
    const api = availableCustomerPrivacy();
    if (api) return Promise.resolve(api);
    if (customerPrivacyLoad) return customerPrivacyLoad;
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return Promise.resolve(null);
    customerPrivacyLoad = new Promise(resolve => {
      try {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
          customerPrivacyLoad = null;
          resolve(error ? null : availableCustomerPrivacy());
        });
      } catch {
        customerPrivacyLoad = null;
        resolve(null);
      }
    });
    return customerPrivacyLoad;
  };
  const parsePurposes = value => {
    const state = Object.fromEntries(purposes.map(key => [key, false]));
    String(value || '').split(',').forEach(pair => {
      const [key, valuePart] = pair.split(':');
      if (purposes.includes(key)) state[key] = valuePart === '1';
    });
    return state;
  };
  const serializePurposes = state => purposes.map(key => `${key}:${state[key] ? '1' : '0'}`).join(',');
  const consentValue = () => {
    const api = customerPrivacy();
    if (!api || typeof api.currentVisitorConsent !== 'function') return {};
    try { return api.currentVisitorConsent() || {}; } catch { return {}; }
  };
  const storedPurposes = () => {
    const api = customerPrivacy();
    if (!api || typeof api.getTrackingConsentMetafield !== 'function') return null;
    try { return api.getTrackingConsentMetafield('finiks_purposes'); } catch { return null; }
  };
  const effectiveState = () => {
    const consent = consentValue();
    const chosen = parsePurposes(storedPurposes());
    const analyticsAllowed = typeof customerPrivacy()?.analyticsProcessingAllowed === 'function' ? customerPrivacy().analyticsProcessingAllowed() : isGranted(consent.analytics);
    const marketingAllowed = typeof customerPrivacy()?.marketingAllowed === 'function' ? customerPrivacy().marketingAllowed() : isGranted(consent.marketing);
    const preferencesAllowed = typeof customerPrivacy()?.preferencesProcessingAllowed === 'function' ? customerPrivacy().preferencesProcessingAllowed() : isGranted(consent.preferences);
    return {
      ...chosen,
      analyticsAllowed: Boolean(analyticsAllowed && (chosen.analytics || chosen.behaviour)),
      marketingAllowed: Boolean(marketingAllowed && (chosen.measurement || chosen.attribution)),
      preferencesAllowed: Boolean(preferencesAllowed && chosen.personalisation)
    };
  };
  const hasDecision = consent => ['analytics', 'marketing', 'preferences'].every(key => isDecision(consent[key]));
  const paint = state => purposes.forEach(key => { const input = preferences.elements.namedItem(key); if (input) input.checked = Boolean(state[key]); });
  const setBusy = busy => {
    controls.forEach(control => { control.disabled = busy; });
    root.setAttribute('aria-busy', String(busy));
  };
  const reveal = () => {
    root.hidden = false;
    banner.hidden = false;
    preferences.hidden = true;
    setBusy(false);
    root.querySelector('[data-finiks-consent-manage]').setAttribute('aria-expanded', 'false');
  };
  const hide = () => { root.hidden = true; };
  const announce = message => { if (status) status.textContent = message; };
  const publish = state => document.dispatchEvent(new CustomEvent('finiks:consent-changed', { detail: state }));
  const showUnavailable = () => {
    root.hidden = false;
    banner.hidden = false;
    preferences.hidden = true;
    setBusy(true);
    announce(root.dataset.unavailableMessage);
  };
  const save = async state => {
    setBusy(true);
    const api = await ensureCustomerPrivacy();
    if (!api) { showUnavailable(); return; }
    const payload = {
      analytics: Boolean(state.analytics || state.behaviour),
      preferences: Boolean(state.personalisation),
      marketing: Boolean(state.measurement || state.attribution),
      metafields: [{ key: 'finiks_purposes', value: serializePurposes(state) }]
    };
    try {
      api.setTrackingConsent(payload, result => {
        setBusy(false);
        if (result && result.error) {
          announce(root.dataset.unavailableMessage);
          return;
        }
        const next = effectiveState();
        paint(next);
        publish(next);
        hide();
      });
    } catch {
      setBusy(false);
      announce(root.dataset.unavailableMessage);
    }
  };
  const manage = () => {
    const state = effectiveState();
    paint(state);
    root.hidden = false;
    banner.hidden = false;
    preferences.hidden = false;
    root.querySelector('[data-finiks-consent-manage]').setAttribute('aria-expanded', 'true');
  };
  const openPreferences = async () => {
    setBusy(true);
    const api = await ensureCustomerPrivacy();
    if (!api) { showUnavailable(); return; }
    setBusy(false);
    manage();
  };
  root.querySelector('[data-finiks-consent-accept]').addEventListener('click', () => { void save(Object.fromEntries(purposes.map(key => [key, true]))); });
  root.querySelector('[data-finiks-consent-reject]').addEventListener('click', () => { void save(Object.fromEntries(purposes.map(key => [key, false]))); });
  root.querySelector('[data-finiks-consent-manage]').addEventListener('click', () => { void openPreferences(); });
  root.querySelector('[data-finiks-consent-close]').addEventListener('click', () => { preferences.hidden = true; root.querySelector('[data-finiks-consent-manage]').setAttribute('aria-expanded', 'false'); });
  preferences.addEventListener('submit', event => { event.preventDefault(); const state = Object.fromEntries(purposes.map(key => [key, Boolean(preferences.elements.namedItem(key)?.checked)])); void save(state); });
  window.FiniksConsent = {
    getState: effectiveState,
    openPreferences,
    requestCamera: async () => {
      if (!effectiveState().camera) throw new Error('CAMERA_CONSENT_REQUIRED');
      return navigator.mediaDevices.getUserMedia({ video: true, audio: false });
    },
    requestMicrophone: async () => {
      if (!effectiveState().microphone) throw new Error('MICROPHONE_CONSENT_REQUIRED');
      return navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    },
    stopStream: stream => stream?.getTracks?.().forEach(track => track.stop())
  };
  const initialise = async () => {
    const api = await ensureCustomerPrivacy();
    if (!api) { showUnavailable(); return; }
    const current = effectiveState();
    paint(current);
    publish(current);
    if (!hasDecision(consentValue())) reveal();
  };
  void initialise();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', launch, { once: true });
  } else {
    launch();
  }
})();

